// ERRANT ZIP CODES (owner, 2026-09-30, D-241): the References → Vendors
// warning, "X vendors have errant ZIP codes, including …", and the filter its
// button sets.
//
// THE RULE, in one place for the app and the mock. A ZIP is errant when it is
// not blank and is none of:
//   - a US ZIP, `NNNNN`;
//   - a US ZIP+4, `NNNNN-NNNN`;
//   - a Canadian postal code, `A1A 1A1` (the space optional). Three real
//     vendors are in Ontario, and their codes are right, so they are not
//     flagged. No other country's code appears in the master.
// A vendor is errant when its primary ZIP or its Remittance Address ZIP is.
//
// The server applies the same rule in SQL, because the Vendors tab loads a
// page of the master rather than all of it: `ERRANT_POSTAL_CODE_SQL` in
// `backend/src/repo/pg/masters.ts`. Both are pinned to the same list of
// vendors in `data/vendors.csv` — `app/mock/tests/vendor-errant-zip.test.ts`
// here and `backend/src/lambda/bootstrap.test.ts` on a real Postgres — so they
// cannot drift apart unnoticed.

import { countOf, sentenceList } from './format'

const VALID_POSTAL_CODE = /^(\d{5}(-\d{4})?|[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d)$/

/** True for a non-blank ZIP the rule above does not accept. */
export function isErrantPostalCode(code: string | null | undefined): boolean {
  const z = (code ?? '').trim()
  return z !== '' && !VALID_POSTAL_CODE.test(z)
}

/** How many vendors the warning names before it says "and N more". */
const ERRANT_ZIP_NAMED = 5

/**
 * The warning's sentence. Each vendor by name first, its ID beside it:
 * `13 vendors have errant ZIP codes, including BL COMPANIES (3582), …, and 8 more.`
 * Up to five are named; when that is all of them the sentence lists them after
 * a colon instead. `total` is the whole count, which can exceed `vendors`
 * when the server returned a page.
 */
export function errantZipSentence(
  vendors: readonly { vendor_id: string; vendor_name?: string | null }[],
  total: number = vendors.length,
): string {
  const named = vendors
    .slice(0, ERRANT_ZIP_NAMED)
    .map((v) => `${v.vendor_name || '(unnamed)'} (${v.vendor_id})`)
  const head = `${countOf(total, 'vendor')} ${total === 1 ? 'has an errant ZIP code' : 'have errant ZIP codes'}`
  if (named.length === 0) return `${head}.`
  if (total <= named.length) return `${head}: ${sentenceList(named)}.`
  // The same joiner, with the remainder as the last item, so one named vendor
  // reads "including X and 8 more" rather than "including X, and 8 more".
  return `${head}, including ${sentenceList([...named, `${total - named.length} more`])}.`
}
